import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Message } from '../../types';
import { MessageBubble } from './MessageBubble';
import { LoadingSpinner } from '../Shared/LoadingSpinner';

interface MessageListProps {
  messages: Message[];
  loading: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  currentUserId: string;
  onReportMessage?: (message: Message) => void;
  onLoadMore?: () => void;
}

export const MessageList: React.FC<MessageListProps> = ({ 
  messages, 
  loading, 
  loadingMore = false,
  hasMore = false,
  currentUserId, 
  onReportMessage,
  onLoadMore
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const previousScrollHeight = useRef<number>(0);
  const isLoadingMoreRef = useRef(false);

  // Intersection Observer for loading more messages when scrolling to top
  useEffect(() => {
    if (!topSentinelRef.current || !onLoadMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !isLoadingMoreRef.current) {
          isLoadingMoreRef.current = true;
          // Store current scroll height before loading more
          if (containerRef.current) {
            previousScrollHeight.current = containerRef.current.scrollHeight;
          }
          onLoadMore();
        }
      },
      { 
        root: containerRef.current,
        threshold: 0.1,
        rootMargin: '100px 0px 0px 0px' // Trigger 100px before reaching top
      }
    );

    observer.observe(topSentinelRef.current);

    return () => observer.disconnect();
  }, [hasMore, loadingMore, onLoadMore]);

  // Maintain scroll position when older messages are loaded
  useEffect(() => {
    if (containerRef.current && previousScrollHeight.current > 0 && isLoadingMoreRef.current) {
      const newScrollHeight = containerRef.current.scrollHeight;
      const scrollDiff = newScrollHeight - previousScrollHeight.current;
      containerRef.current.scrollTop = scrollDiff;
      previousScrollHeight.current = 0;
      isLoadingMoreRef.current = false;
    }
  }, [messages.length]);

  // Track if user is at bottom of chat
  const handleScroll = useCallback(() => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      const atBottom = scrollHeight - scrollTop - clientHeight < 50;
      setIsAtBottom(atBottom);
    }
  }, []);

  // Auto-scroll to bottom only when new messages arrive and user is at bottom
  useEffect(() => {
    if (containerRef.current && isAtBottom && !isLoadingMoreRef.current) {
      const { scrollHeight, clientHeight } = containerRef.current;
      containerRef.current.scrollTop = scrollHeight - clientHeight;
    }
  }, [messages, isAtBottom]);

  // Initial scroll to bottom
  useEffect(() => {
    if (containerRef.current && messages.length > 0 && !loading) {
      const { scrollHeight, clientHeight } = containerRef.current;
      containerRef.current.scrollTop = scrollHeight - clientHeight;
    }
  }, [loading, messages.length]);

  if (loading && messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      onScroll={handleScroll}
      className="flex-1 overflow-y-auto p-4 bg-gray-50 flex flex-col"
    >
      {/* Top sentinel for infinite scroll trigger */}
      {onLoadMore && (
        <div ref={topSentinelRef} className="flex-shrink-0">
          {loadingMore && (
            <div className="flex justify-center py-3">
              <div className="flex items-center gap-2 text-gray-500">
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-sm">Loading earlier messages...</span>
              </div>
            </div>
          )}
          {!hasMore && messages.length > 0 && (
            <div className="flex justify-center py-3">
              <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                Beginning of conversation
              </span>
            </div>
          )}
        </div>
      )}
      
      <div className="flex-1" /> 
      {messages.length === 0 ? ( 
        <div className="text-center text-gray-400 text-sm py-10">Start the conversation...</div>
      ) : (
        messages.map(msg => (
          <MessageBubble 
            key={msg.id} 
            message={msg} 
            isOwnMessage={msg.senderId === currentUserId}
            onReportMessage={onReportMessage}
          />
        ))
      )}
    </div>
  );
};
